import React, { FC } from 'react';
import Lozenge from '@atlaskit/lozenge';
import { SloDTO } from '@atlassiansox/phobos-client-ts';

import { PopupLink, ExternalLinkAction } from '@/components/primitive';
import { DAC_DOCS_INTRO_URL, DAC_FAQS_DOC_LINK } from '@/constants';

import { getErrorBudget, formatPerformanceResult, formatErrorBudget } from '../../common/utils';
import {
  SLO_CALCULATION_DECIMAL_PLACES,
  ERROR_BUDGET_CALCULATION_DECIMAL_PLACES,
} from '../../common/constants';

import * as styles from './styles';

type ErrorBudgetHelperProps = Pick<SloDTO, 'performance' | 'objective'>;

const ErrorBudgetHelper: FC<ErrorBudgetHelperProps> = ({ performance, objective }) => {
  const result = performance && performance.result;
  if (!result) return <></>;

  const sliDisplay: string = formatPerformanceResult(result);
  const sliCalculation: string = formatPerformanceResult(result, SLO_CALCULATION_DECIMAL_PLACES);
  const sliEqSign = sliDisplay === sliCalculation ? '=' : <>&asymp;</>;

  const errorBudget: number | undefined = getErrorBudget(result, objective);
  const errorBudgetDisplay: string = formatErrorBudget(getErrorBudget(result, objective), {
    truncate: false,
  });
  const errorBudgetCalculation: string = formatErrorBudget(
    getErrorBudget(result, objective),
    {
      truncate: false,
    },
    ERROR_BUDGET_CALCULATION_DECIMAL_PLACES
  );
  const errorBudgetEqSign = errorBudgetDisplay === errorBudgetCalculation ? '=' : <>&asymp;</>;

  return (
    <PopupLink title="Show calculations" icon={<span css={styles.calculationIconStyles}>Σ</span>}>
      <p>
        <Lozenge>error budget</Lozenge> is calculated as:
      </p>
      <p />
      <div css={styles.equationStyles}>
        <div css={styles.equationRowStyles}>
          <div css={styles.equationColumnStyles}>
            <div css={styles.equationCellStyles}>
              <span>
                <b>performance</b> (28 Days)
              </span>
              <span>-</span>
              <span>objective</span>
            </div>
            <div css={styles.equationLineStyles} />
            <div css={styles.equationCellStyles}>
              <span>1</span>
              <span>-</span>
              <span>objective</span>
            </div>
          </div>
          <div>&#x2715;</div>
          <div>100 %</div>
          <div>=</div>
          <div css={styles.equationColumnStyles}>
            <div css={styles.equationCellStyles}>
              <span>
                <b>{sliCalculation}</b>
              </span>
              <span>-</span>
              <span>{objective}</span>
            </div>
            <div css={styles.equationLineStyles} />
            <div css={styles.equationCellStyles}>
              <span>100</span>
              <span>-</span>
              <span>{objective}</span>
            </div>
          </div>
          <div>%</div>
          <div>{errorBudgetEqSign}</div>
          <div>
            <span>
              <b>{errorBudgetDisplay}</b>
            </span>
            <span>&nbsp;%</span>
          </div>
        </div>
      </div>
      <p>
        <Lozenge>SLI</Lozenge> (performance) in above equation is calculated as:
      </p>
      <p />
      <div css={styles.equationStyles}>
        <div css={styles.equationRowStyles}>
          <div css={styles.equationColumnStyles}>
            <div css={styles.equationCellStyles}>
              <span>good events (28 days)</span>
            </div>
            <div css={styles.equationLineStyles} />
            <div css={styles.equationCellStyles}>
              <span>good</span>
              <span>+</span>
              <span>bad events (28 days)</span>
            </div>
          </div>
          <div>&#x2715;</div>
          <div>100 %</div>
          <div>=</div>
          <div css={styles.equationColumnStyles}>
            <div css={styles.equationCellStyles}>
              <span>{performance?.goodCount}</span>
            </div>
            <div css={styles.equationLineStyles} />
            <div css={styles.equationCellStyles}>
              <span>{performance?.goodCount}</span>
              <span>+</span>
              <span>{performance?.badCount}</span>
            </div>
          </div>
          <div>%</div>
          <div>{sliEqSign}</div>
          <div>
            <span>
              <b>{sliDisplay}</b>
            </span>
            <span>&nbsp;%</span>
          </div>
        </div>
      </div>
      <p>
        Hence <b>error budget</b> is calculated by the <b>SLI</b> over a <b>28 days</b> rolling
        window. See&nbsp;
        <ExternalLinkAction text="docs" href={`${DAC_DOCS_INTRO_URL}glossary/#error-budget`} />
      </p>
      {errorBudget && errorBudget < 100 && (
        <>
          <p />
          <div css={styles.faqStyles}>
            <p>
              <b>FAQs</b>
            </p>
            {errorBudget < 0 && (
              <p>
                Why is my SLO still in breach even though I fixed the issue?{' '}
                <ExternalLinkAction
                  text=""
                  href={`${DAC_FAQS_DOC_LINK}slos/#i-fixed-the-issue-that-caused-an-slo-breach--why-am-i-still-in-breach-`}
                />
              </p>
            )}
            <p>
              Why is my error budget decreasing with no bad events in the last few days?
              <ExternalLinkAction
                text=""
                href={`${DAC_FAQS_DOC_LINK}slos/#why-is-my-error-budget-decreasing-even-though-i-haven-t-had-any-bad-events-in-the-last-few-days-`}
              />
            </p>
          </div>
        </>
      )}
    </PopupLink>
  );
};

export default ErrorBudgetHelper;
